import { useEffect, useState } from "react";
import { X as XIcon, Buildings, House } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { COBERTURAS } from "./valoracionData";

/**
 * Modal de creación / edición de un grupo de valoración. Replica el flujo del
 * ERP de referencia: Interés Asegurado · Cobertura · Límite · Franquicia ·
 * Nombre del grupo · Excluir · Configuración de tabla (4 toggles).
 *
 * Para `tipo='causante'` el campo Interés se oculta.
 *
 * Cuando se activa "Con baremos" y la aseguradora tiene baremos de
 * reparación dados de alta (Ocaso → Pintura, Albañilería, …), se muestra
 * un dropdown para fijar QUÉ baremo concreto usa este grupo. Ese baremo
 * se persiste en `baremo_rep_id` y se preselecciona al importar partidas.
 */
export default function ValoracionGroupModal({ open, tipo, initial = null, onClose, onSave, implicados = [], aseguradoraId = null }) {
  const isCausante = tipo === "causante";
  const isPerjudicado = tipo === "perjudicado";
  const [interes, setInteres] = useState(initial?.interes || (isCausante ? null : "continente"));
  const [cobertura, setCobertura] = useState(initial?.cobertura || "");
  const [limite, setLimite] = useState(initial?.limite ?? "");
  const [franquicia, setFranquicia] = useState(initial?.franquicia ?? "");
  const [franqTipo, setFranqTipo] = useState(initial?.franquicia_tipo || "eur");
  const [nombre, setNombre] = useState(initial?.nombre_grupo || "");
  const [implicadoId, setImplicadoId] = useState(initial?.implicado_id || "");
  const [excluir, setExcluir] = useState(!!initial?.excluir);
  const [resolucion, setResolucion] = useState(initial?.resolucion || "");
  const [conBaremos, setConBaremos] = useState(!!initial?.con_baremos);
  const [baremoRepId, setBaremoRepId] = useState(initial?.baremo_rep_id || "");
  const [aplicarDep, setAplicarDep] = useState(!!initial?.aplicar_depreciaciones);
  const [introIva, setIntroIva] = useState(!!initial?.introducir_iva);
  const [introRecl, setIntroRecl] = useState(!!initial?.introducir_reclamados);
  const [busy, setBusy] = useState(false);
  const [baremos, setBaremos] = useState([]);

  // Carga los baremos de reparación de la aseguradora actual cuando el
  // toggle "Con baremos" se enciende y aún no los tenemos cacheados.
  useEffect(() => {
    if (!open || !conBaremos || !aseguradoraId || baremos.length > 0) return;
    let cancel = false;
    api.get(`/aseguradoras/${aseguradoraId}/baremos-reparacion?activo=true`)
      .then((r) => { if (!cancel) setBaremos(r.data?.baremos || r.data || []); })
      .catch(() => { if (!cancel) setBaremos([]); });
    return () => { cancel = true; };
  }, [open, conBaremos, aseguradoraId, baremos.length]);

  useEffect(() => {
    if (!open) return;
    setInteres(initial?.interes || (isCausante ? null : "continente"));
    setCobertura(initial?.cobertura || "");
    setLimite(initial?.limite ?? "");
    setFranquicia(initial?.franquicia ?? "");
    setFranqTipo(initial?.franquicia_tipo || "eur");
    setNombre(initial?.nombre_grupo || "");
    setImplicadoId(initial?.implicado_id || "");
    setExcluir(!!initial?.excluir);
    setResolucion(initial?.resolucion || "");
    setConBaremos(!!initial?.con_baremos);
    setBaremoRepId(initial?.baremo_rep_id || "");
    setAplicarDep(!!initial?.aplicar_depreciaciones);
    setIntroIva(!!initial?.introducir_iva);
    setIntroRecl(!!initial?.introducir_reclamados);
  }, [open, initial, isCausante]);

  if (!open) return null;

  const handleSave = async () => {
    setBusy(true);
    try {
      await onSave({
        tipo,
        implicado_id: implicadoId || null,
        interes: isCausante ? null : (interes || null),
        cobertura: cobertura || null,
        limite: limite === "" ? null : Number(limite),
        franquicia: franquicia === "" ? null : Number(franquicia),
        franquicia_tipo: franqTipo,
        nombre_grupo: nombre || null,
        excluir,
        resolucion: resolucion || null,
        con_baremos: conBaremos,
        baremo_rep_id: conBaremos ? (baremoRepId || null) : null,
        aplicar_depreciaciones: aplicarDep,
        introducir_iva: introIva,
        introducir_reclamados: introRecl,
      });
      onClose?.();
    } finally { setBusy(false); }
  };

  const tipoLabel = { asegurado: "Asegurado", perjudicado: "Perjudicado", causante: "Causante" }[tipo];

  return (
    <div
      data-testid="valoracion-group-modal"
      className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm flex items-center justify-center p-6 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-amber-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-600 font-bold">
              {initial ? "Editar grupo" : "Nuevo grupo"}
            </div>
            <h3 className="font-display font-bold text-lg text-navy tracking-tight">
              Valoración · {tipoLabel}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid="vg-close">
            <XIcon className="w-5 h-5" weight="bold" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Implicado (sólo perjudicado / causante) */}
          {(isPerjudicado || isCausante) && (
            <Field
              label={isCausante ? "Causante asociado" : "Perjudicado asociado"}
              className=""
            >
              <select
                value={implicadoId}
                onChange={(e) => setImplicadoId(e.target.value)}
                data-testid="vg-implicado"
                className="!text-[13px] !py-1.5 w-full"
              >
                <option value="">— sin asociar —</option>
                {(implicados || [])
                  .filter((x) => x.tipo === tipo)
                  .map((it) => (
                    <option key={it.id} value={it.id}>
                      {it.nombre || "(sin nombre)"}
                      {it.nif ? ` · ${it.nif}` : ""}
                    </option>
                  ))}
              </select>
              {(implicados || []).filter((x) => x.tipo === tipo).length === 0 && (
                <div className="text-[11px] text-amber-700 mt-1">
                  No hay {tipo === "causante" ? "causantes" : "perjudicados"} registrados en el expediente. Añádelos en la pestaña Datos.
                </div>
              )}
            </Field>
          )}

          {/* Interés Asegurado + Cobertura */}
          <div className="grid grid-cols-2 gap-4">
            {!isCausante && (
              <Field label="Interés asegurado">
                <div className="flex gap-2">
                  <SegBtn active={interes === "continente"} onClick={() => setInteres("continente")} testid="vg-interes-continente">
                    <Buildings className="w-3.5 h-3.5" weight="fill" /> Continente
                  </SegBtn>
                  <SegBtn active={interes === "contenido"} onClick={() => setInteres("contenido")} testid="vg-interes-contenido">
                    <House className="w-3.5 h-3.5" weight="fill" /> Contenido
                  </SegBtn>
                </div>
              </Field>
            )}
            <Field label="Cobertura" className={isCausante ? "col-span-2" : ""}>
              <select
                value={cobertura}
                onChange={(e) => setCobertura(e.target.value)}
                data-testid="vg-cobertura"
                className="!text-[13px] !py-1.5 w-full"
              >
                <option value="">— selecciona —</option>
                {COBERTURAS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          {/* Límite, Franquicia (€/%), Nombre */}
          <div className="grid grid-cols-12 gap-3">
            <Field label="Límite (€)" className="col-span-3">
              <input type="number" min="0" step="0.01" value={limite}
                onChange={(e) => setLimite(e.target.value)}
                data-testid="vg-limite"
                placeholder="Sin límite" className="!text-[13px] !py-1.5 w-full" />
            </Field>
            <Field label="Franquicia" className="col-span-3">
              <input type="number" min="0" step="0.01" value={franquicia}
                onChange={(e) => setFranquicia(e.target.value)}
                data-testid="vg-franquicia"
                placeholder="0" className="!text-[13px] !py-1.5 w-full" />
            </Field>
            <Field label=" " className="col-span-2">
              <select value={franqTipo} onChange={(e) => setFranqTipo(e.target.value)}
                data-testid="vg-franquicia-tipo" className="!text-[13px] !py-1.5 w-full">
                <option value="eur">€</option>
                <option value="pct">%</option>
              </select>
            </Field>
            <Field label="Nombre del grupo" className="col-span-4">
              <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)}
                data-testid="vg-nombre" placeholder={`p. ej. ${tipoLabel} · Continente`}
                className="!text-[13px] !py-1.5 w-full" />
            </Field>
          </div>

          {/* Excluir */}
          <label className="flex items-center gap-2 text-[13px] text-navy-soft cursor-pointer select-none">
            <input type="checkbox" checked={excluir} onChange={(e) => setExcluir(e.target.checked)}
              data-testid="vg-excluir" className="w-4 h-4" />
            Excluir esta valoración al completo
          </label>

          {/* Forma de resolución (Indemniza / Asistencia de compañía) */}
          <fieldset className="border border-slate-200 rounded-lg px-4 py-3">
            <legend className="text-[10px] uppercase tracking-widest font-bold text-amber-700 px-2">
              Forma de resolución
            </legend>
            <div className="flex flex-wrap gap-2 mt-1">
              <label
                data-testid="vg-resolucion-indemnizacion"
                className={`inline-flex items-center gap-2 text-[12px] font-semibold border rounded-lg px-3 py-1.5 cursor-pointer ${
                  resolucion === "indemnizacion"
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                }`}
              >
                <input
                  type="radio"
                  name={`resolucion-${initial?.id || "new"}`}
                  className="hidden"
                  checked={resolucion === "indemnizacion"}
                  onChange={() => setResolucion("indemnizacion")}
                />
                Indemnización
              </label>
              <label
                data-testid="vg-resolucion-asistencia"
                className={`inline-flex items-center gap-2 text-[12px] font-semibold border rounded-lg px-3 py-1.5 cursor-pointer ${
                  resolucion === "asistencia"
                    ? "bg-sky-600 text-white border-sky-600"
                    : "bg-white text-sky-700 border-sky-200 hover:bg-sky-50"
                }`}
              >
                <input
                  type="radio"
                  name={`resolucion-${initial?.id || "new"}`}
                  className="hidden"
                  checked={resolucion === "asistencia"}
                  onChange={() => setResolucion("asistencia")}
                />
                Asistencia de compañía
              </label>
              {resolucion && (
                <button
                  type="button"
                  data-testid="vg-resolucion-reset"
                  onClick={() => setResolucion("")}
                  className="text-[11px] text-slate-500 hover:text-slate-700 underline"
                >
                  Quitar decisión
                </button>
              )}
            </div>
            <p className="mt-1.5 text-[11px] text-slate-500">
              Se mostrará como badge en la tabla del informe y contribuirá al
              desglose «Importe indemnizable» vs «Atendido por asistencia»
              del resumen económico.
            </p>
          </fieldset>

          {/* Configuración de la tabla */}
          <fieldset className="border border-slate-200 rounded-lg px-4 py-3">
            <legend className="text-[10px] uppercase tracking-widest font-bold text-amber-700 px-2">
              Configuración de la tabla
            </legend>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <Toggle checked={conBaremos} onChange={setConBaremos} testid="vg-baremos">
                Con baremos
              </Toggle>
              <Toggle checked={aplicarDep} onChange={setAplicarDep} testid="vg-depreciaciones">
                Aplicar depreciaciones
              </Toggle>
              <Toggle checked={introIva} onChange={setIntroIva} testid="vg-iva">
                Introducir IVA
              </Toggle>
              <Toggle checked={introRecl} onChange={setIntroRecl} testid="vg-reclamados">
                Introducir importes reclamados
              </Toggle>
            </div>

            {/* Selector de baremo concreto cuando "Con baremos" está activo */}
            {conBaremos && aseguradoraId && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <label className="block text-[11px] uppercase tracking-wider font-bold text-amber-700 mb-1">
                  Baremo a usar
                </label>
                {baremos.length === 0 ? (
                  <div className="text-[11px] text-slate-500 italic py-1.5">
                    Esta aseguradora aún no tiene baremos de reparación. Súbelos
                    en <i>Aseguradoras → Baremos de reparación</i>.
                  </div>
                ) : (
                  <select
                    value={baremoRepId}
                    onChange={(e) => setBaremoRepId(e.target.value)}
                    data-testid="vg-baremo-rep"
                    className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 bg-white"
                  >
                    <option value="">— Cualquiera (elegir al importar) —</option>
                    {baremos.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.nombre}{b.year ? ` (${b.year})` : ""}
                      </option>
                    ))}
                  </select>
                )}
                <p className="mt-1.5 text-[11px] text-slate-500">
                  Al importar partidas, este baremo vendrá preseleccionado y
                  podrás buscar la partida concreta por texto.
                </p>
              </div>
            )}
          </fieldset>
        </div>

        <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} data-testid="vg-cancel"
            className="px-3.5 py-2 rounded-md text-[13px] text-slate-600 hover:bg-slate-100">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={busy} data-testid="vg-save"
            className="bg-brand text-white px-4 py-2 rounded-md text-[13px] font-semibold hover:bg-brand-strong disabled:opacity-50">
            {busy ? "Guardando…" : initial ? "Guardar cambios" : "Crear grupo"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="!text-[10px] !uppercase !tracking-widest !text-muted !font-semibold block mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function SegBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold border transition-colors ${
        active
          ? "bg-brand text-white border-brand"
          : "bg-white text-navy-soft border-slate-200 hover:border-slate-300"
      }`}
    >
      {children}
    </button>
  );
}

function Toggle({ checked, onChange, children, testid }) {
  return (
    <label className="flex items-center gap-2 text-[12px] text-navy-soft cursor-pointer select-none">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        data-testid={testid} className="w-4 h-4" />
      {children}
    </label>
  );
}
